import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  CheckoutStepUrlMap,
  WindowService,
  getNewUrl,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  selectOnSessionCompleteUrl,
  selectOnSessionExitUrl,
  selectUrl,
} from '@arc/unified-payments-frontend/shared-stores';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PaymentFooterActions, selectCheckoutStep } from '..';
import { EMPTY, exhaustMap, filter, of } from 'rxjs';
import { selectCurrentLanguage } from '@arc/unified-payments-frontend/i18n';
import { TranslateService } from '@ngx-translate/core';
import { GetTranslationsSuccess } from '../actions/payment-footer.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { Router } from '@angular/router';
import { AppConfigSettings } from '@arc/arc-shared-frontend';

@Injectable()
export class PaymentFooterEffects {
  constructor(
    private store: Store,
    private router: Router,
    private windowService: WindowService,
    private actions$: Actions<PaymentFooterActions.PaymentFooterActionsUnion>,
    private translateService: TranslateService,
    private appSettings: AppConfigSettings,
  ) {}

  private translate(token: string) {
    return this.translateService.instant(token) as string;
  }

  scrollToTop$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PaymentFooterActions.CurrentCheckoutStepChangedAction),
        exhaustMap(() => {
          this.windowService.setScrollPositionOfElement(
            document.getElementById('checkout-make-payments'),
            0,
            0,
          );
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  returnToApplicationStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PaymentFooterActions.ExecuteReturnToAppAction),
        concatLatestFrom(() => [this.store.select(selectOnSessionCompleteUrl)]),
        exhaustMap(([, url]) => {
          this.windowService.setLocation(url);
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  exitSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PaymentFooterActions.ExecuteSessionExitAction),
        concatLatestFrom(() => [this.store.select(selectOnSessionExitUrl)]),
        exhaustMap(([, url]) => {
          this.windowService.setLocation(url);
          return EMPTY;
        }),
      ),

    { dispatch: false },
  );

  getTranslations$ = createEffect(() =>
    this.store.select(selectCurrentLanguage).pipe(
      exhaustMap((currentLanguage) =>
        this.translateService.use(currentLanguage).pipe(
          exhaustMap(() =>
            of(
              GetTranslationsSuccess({
                translations: {
                  reviewPayment: this.translate('ARC_PAYMENT_REVIEW_PAYMENT'),
                  contactInformation: this.translate(
                    'ARC_PAYMENT_CONTACT_INFORMATION',
                  ),
                  subscriberAgreement: this.translate(
                    'ARC_PAYMENT_SUBSCRIBER_AGREEMENT',
                  ),
                  back: this.translate('ARC_PAYMENT_BACK'),
                  proceedToPayment: this.translate(
                    'ARC_PAYMENT_PROCEED_TO_PAYMENT',
                  ),
                  authorizePayment: this.translate(
                    'ARC_PAYMENT_AUTHORIZE_PAYMENT',
                  ),
                  printConfirmation: this.translate(
                    'ARC_PAYMENT_PRINT_CONFIRMATION',
                  ),
                  alternativePaymentMethod: this.translate(
                    'ARC_PAYMENT_ALTERNATIVE_PAYMENT_METHOD',
                  ),
                  return: this.translate('ARC_PAYMENT_RETURN'),
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  navigateToNextStep$ = createEffect(
    () =>
      this.store.select(selectCheckoutStep).pipe(
        filter(
          (step) =>
            step != null &&
            this.appSettings.getconfig('navigateOnStepChange') === 'true',
        ),
        concatLatestFrom(() => [this.store.select(selectUrl)]),
        exhaustMap(([currentStep, route]) => {
          const newUrl = getNewUrl(route, CheckoutStepUrlMap[currentStep]);
          this.router.navigate([newUrl]);
          return of();
        }),
      ),
    { dispatch: false },
  );
}
